import { getTranslatedMessage } from "./i18n";

// Loader configuration
export const LOADER_DELAY = 500; // Configurable delay before showing loader

type ApiNotificationConfig =
  | { endpoint: string; method: string; statusCode?: number }
  | { endpointParts: string[]; method: string; statusCode?: number };

// Not exported: isNotificationExcluded below is the only consumer, and the
// only supported way to ask whether a failure should stay silent.
const ExcludedAPIsNotification: ApiNotificationConfig[] = [
  {
    endpoint: "exchange-operations",
    method: "DELETE"
  },
  {
    endpoint: "verifications/phone/validate",
    method: "POST"
  },
  {
    endpoint: "/v2/journey",
    method: "POST"
  },
  {
    endpoint: "bookings",
    method: "PATCH"
  },
  {
    endpoint: "/exchange-offers/upgrades",
    method: "POST"
  },
  // Voucher and loyalty-program backends are not owned by this app; their
  // failures are expected noise, so they must not raise the generic toast.
  //
  // Read-only voucher fetches — nothing for the user to act on.
  {
    endpoint: "/vouchers",
    method: "GET"
  },
  // Booking-scoped voucher apply/remove. Deliberately NOT a bare "/vouchers"
  // match: that would also swallow POST /customers/{id}/vouchers (add voucher
  // in My Account), which has no inline error surface and would fail silently.
  // These two do surface inline via voucherErrorAtom in ApplyVoucher.
  {
    endpointParts: ["/bookings/", "/vouchers/"],
    method: "POST"
  },
  {
    endpointParts: ["/bookings/", "/vouchers/"],
    method: "DELETE"
  },
  // Background lookups — loyalty degrades to hidden/zero-point UI with nothing
  // for the user to act on. Their failure branches console.warn, which reaches
  // Sentry via consoleLoggingIntegration, so the team still sees the outage.
  {
    endpoint: "/loyalty-program",
    method: "GET"
  },
  // Unreachable today — the only calls are commented out in MyAccountInitStep,
  // because loyalty is closed to sign-ups and the Join CTA is hidden (see
  // AccountDetails). Re-check this entry when that flow is restored: a failed
  // enrollment is a deliberate user action and needs feedback of its own.
  {
    endpoint: "/loyalty-program",
    method: "POST"
  },
  // Substring match, so this covers /metadata/customers/tiers-config too.
  {
    endpoint: "/metadata/customers/tiers",
    method: "GET"
  }
];

// Use SpecialErrorNotificationAPIs when Need to display Error Message coming from API.
export const SpecialErrorNotificationAPIs = [
  {
    endpoint: "verifications/phone",
    method: "POST"
  }
];

// Returns true when the failing request must not raise an error toast.
export function isNotificationExcluded(endpoint: string, method: string, statusCode?: number): boolean {
  return ExcludedAPIsNotification.some(api => {
    if (method !== api.method) return false;

    if (api.statusCode && statusCode !== api.statusCode) {
      return false;
    }

    if ("endpointParts" in api) {
      return api.endpointParts.every(part => endpoint.includes(part));
    }

    return endpoint.includes(api.endpoint);
  });
}

export function isApiMatch(endpoint: string, method: string, apiList: { endpoint: string, method: string }[]) {
  return apiList.some(api => endpoint.includes(api.endpoint) && method === api.method);
}

// This method is used for Custom Message with translated value.
export function getCustomApiErrorMessage(endpoint: string, method: string, errorCode: string): string | undefined {
  const customErrorMap: Array<{
    endpoint: string;
    method: string;
    errorCode: string;
    translationKey: string;
  }> = [
      {
        endpoint: "/offers",
        method: "POST",
        errorCode: "TRIP_NO_SEARCH_RESULT",
        translationKey: "noTripsAvailable"
      }
    ];
  const found = customErrorMap.find(
    (item) =>
      endpoint.includes(item.endpoint) &&
      method.toUpperCase() === item.method.toUpperCase() &&
      errorCode === item.errorCode
  );
  return found ? getTranslatedMessage(found.translationKey) : undefined;
}

export function getApiErrorMessageFromCode(errorCode?: string): string | undefined {
  if (!errorCode) return undefined;

  const translationKey = `apiErrors.${errorCode}`;
  const message = getTranslatedMessage(translationKey);

  return message && message != translationKey ? message : undefined;
}